import type {
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from "@medplum/fhirtypes";
import type { MemberId, QuestionnaireAnswer } from "@repo/domain";
import { extensionUrl } from "@repo/fhir-systems";
import { answerFromContent, contentFromAnswer } from "../../fhir/questionnaire-answer";
import { omitUndefined } from "../omit-undefined";

// ============================================================
// Answers ⇄ QuestionnaireResponse items, for every model that keeps a
// patient's answers as a response: a questionnaire instance's own row, and the
// response a consent contains.
// ============================================================

/**
 * Which repetition of a repeating group an answer belongs to. Written
 * explicitly rather than inferred from the position of a repeated group item,
 * because clearing occurrence 1 of 3 would otherwise renumber the survivors on
 * the next write and silently move two patients' answers.
 */
export const QUESTIONNAIRE_ANSWER_OCCURRENCE_URL = extensionUrl("questionnaire-answer-occurrence");

function occurrenceOf(item: QuestionnaireResponseItem): number | undefined {
  return item.extension?.find((e) => e.url === QUESTIONNAIRE_ANSWER_OCCURRENCE_URL)?.valueInteger;
}

/** Every answer in the tree, whatever depth a previous writer nested it at. */
export function answersFromItems(
  row: QuestionnaireResponse,
  items: QuestionnaireResponseItem[] | undefined,
  into: QuestionnaireAnswer[] = [],
): QuestionnaireAnswer[] {
  for (const item of items ?? []) {
    const occurrence = occurrenceOf(item);
    for (const answer of item.answer ?? []) {
      const value = contentFromAnswer(row, answer);
      if (value) into.push(omitUndefined({ questionId: item.linkId as MemberId, occurrence, value }));
      answersFromItems(row, answer.item, into);
    }
    answersFromItems(row, item.item, into);
  }
  return into;
}

/**
 * One item per answered address. Several answers sharing a `questionId` and
 * `occurrence` — which is what a `Multiple` choice produces — become several
 * entries in one item's `answer[]`, so every question has one shape.
 *
 * `text` on a stored item is carried across rather than recomputed: the label
 * is denormalized beside the answer so a rendered record can never show the
 * wrong question, and only something holding the definition can write it.
 */
export function answersToItems(
  answers: readonly QuestionnaireAnswer[],
  prior?: QuestionnaireResponseItem[],
): QuestionnaireResponseItem[] {
  const priorText = new Map<string, string>();
  for (const item of flattenItems(prior)) {
    if (item.text) priorText.set(addressKey(item.linkId, occurrenceOf(item)), item.text);
  }

  const byAddress = new Map<string, QuestionnaireResponseItem>();
  for (const answer of answers) {
    const key = addressKey(answer.questionId, answer.occurrence);
    const item =
      byAddress.get(key) ??
      omitUndefined({
        linkId: answer.questionId as string,
        text: priorText.get(key),
        extension:
          answer.occurrence === undefined
            ? undefined
            : [{ url: QUESTIONNAIRE_ANSWER_OCCURRENCE_URL, valueInteger: answer.occurrence }],
        answer: [] as QuestionnaireResponseItemAnswer[],
      });
    item.answer?.push(answerFromContent(answer.value));
    byAddress.set(key, item);
  }
  return [...byAddress.values()];
}

function flattenItems(
  items: QuestionnaireResponseItem[] | undefined,
  into: QuestionnaireResponseItem[] = [],
): QuestionnaireResponseItem[] {
  for (const item of items ?? []) {
    into.push(item);
    flattenItems(item.item, into);
    for (const answer of item.answer ?? []) flattenItems(answer.item, into);
  }
  return into;
}

function addressKey(questionId: string, occurrence: number | undefined): string {
  return `${questionId}#${occurrence === undefined ? "" : occurrence}`;
}
